import { ComponentType, ReactNode } from 'react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route as ReactRouterRoute,
} from 'react-router-dom'
import { ProtectedRoute } from 'components'
import { AuthLayout } from 'layout'
import {
  Analytics,
  AnalyticsDetails,
  BakersProductsPage,
  Blends,
  Data,
  DeviceSN,
  DeviceTypes,
  EquinomBins,
  FlourProductsPage,
  IncomingDeliveriesDetails,
  LoginPage,
  NoPermission,
  PageNotFound,
  ResetPasswordPage,
  Shipment,
  StoredDeliveriesDetails,
  SystemSettings,
  Users,
  WheatCodes,
  WheatProductsPage,
} from 'pages'
import { UnavailablePage } from 'pages/uiPages/UnavailablePage'
import { BlendDetails } from 'pages/Blends/pages/BlendDetails/BlendDetails'
import { SavedBlendDetails } from 'pages/Blends/pages/SavedBlendDetails/SavedBlendDetails'
import { ForgotPasswordPage } from '../pages/AuthPages/ForgotPasswordPage/ForgotPasswordPage'
import { WithWheatPolling } from 'components/polling/WheatPolling'
import { isProd } from '../utils/env'
import Admin from '../pages/Admin/Admin'
import {
  PermissionsRouteGuard,
  SettingsRouteGuard,
} from '../components/ProtectedRoute'
import { CrudAction, Entity } from '../types/permissions'
import { RouteKeys, SVGComponent } from '../types'
import BlendsIcon from 'assets/svgComponents/BlendsIcon'
import DataIcon from 'assets/svgComponents/DataIcon'
import SettingsIcon from 'assets/svgComponents/SettingsIcon'
import TruckIcon from 'assets/svgComponents/TruckIcon'
import FlourIcon from 'assets/svgComponents/FlourIcon'
import UserIcon from 'assets/svgComponents/UserIcon'
import AdminIcon from '../assets/svgComponents/AdminIcon'
import ParametersIcon from '../assets/svgComponents/ParametersIcon'
import SiteDetails from '../pages/Settings/Wheat/SiteDetails/SiteDetails'
import { isModuleHidden, SidebarModuleFilter } from './helpers'
import ShipmentsData from '../pages/DataAnalysis/pages/ShipmentsData'
import StorageData from '../pages/DataAnalysis/pages/StorageData'
import BlendsData from '../pages/DataAnalysis/pages/BlendsData'
import FloursData from '../pages/DataAnalysis/pages/FloursData'
import TermsOfUse from '../pages/Legal/TermsOfUse'

export interface RouteConfig {
  path: string
  element?: ReactNode
  guard?: {
    type: 'settings' | 'permissions'
    key: string
    action?: CrudAction | string | (CrudAction | string)[]
  }
  wrapper?: ComponentType<{ children: ReactNode }>
  redirect?: string
  children?: RouteConfig[]
  key?: string
  label?: string
  icon?: SVGComponent
  settings?: string[]
  permissions?: {
    entity: Entity
    action: CrudAction | (CrudAction | string)[] | string
  }
  notAvailable?: boolean
  showInSidebar?: boolean
  module?: SidebarModuleFilter
}

export const routesConfig: RouteConfig[] = [
  {
    path: '/auth',
    element: <AuthLayout />,
    showInSidebar: false,
    children: [
      {
        path: 'login',
        element: <LoginPage />,
        showInSidebar: false,
      },
      {
        path: 'reset-password/:token',
        element: <ResetPasswordPage />,
        showInSidebar: false,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
        showInSidebar: false,
      },
      {
        path: 'terms-of-use',
        element: <TermsOfUse />,
        showInSidebar: false,
      },
    ],
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    showInSidebar: false,
    children: [
      {
        path: '',
        redirect: '/shipments',
        showInSidebar: false,
      },
      {
        path: 'shipments',
        key: RouteKeys.Shipments,
        guard: {
          type: 'settings',
          key: 'SHOW_WHEAT',
        },
        children: [
          {
            path: '',
            element: <Shipment />,
            wrapper: (props) => (
              <WithWheatPolling>{props.children}</WithWheatPolling>
            ),
            guard: {
              type: 'permissions',
              key: Entity.DELIVERY,
              action: [CrudAction.GET, 'get_stored'],
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Shipments',
            key: RouteKeys.Shipments,
            icon: TruckIcon,
            settings: ['SHOW_WHEAT'],
            permissions: {
              entity: Entity.DELIVERY,
              action: [CrudAction.GET, 'get_stored'],
            },
          },
          {
            path: 'incoming-deliveries/:deliveryId/:wheatCodeId',
            element: <IncomingDeliveriesDetails />,
            guard: {
              type: 'permissions',
              key: Entity.DELIVERY,
            },
            showInSidebar: false,
          },
          {
            path: 'stored-deliveries/:deliveryId/:wheatCodeId',
            element: <StoredDeliveriesDetails />,
            guard: {
              type: 'permissions',
              key: Entity.DELIVERY,
            },
            showInSidebar: false,
          },
        ],
      },
      {
        path: 'blends',
        guard: {
          type: 'settings',
          key: 'SHOW_WHEAT',
        },
        children: [
          {
            path: '',
            element: <Blends />,
            guard: {
              type: 'permissions',
              key: Entity.BLENDING,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Blends',
            key: RouteKeys.Blends,
            icon: BlendsIcon,
            settings: ['SHOW_WHEAT'],
            permissions: {
              entity: Entity.BLENDING,
              action: CrudAction.GET,
            },
          },
          {
            path: ':blendId/:productId',
            element: <BlendDetails />,
            guard: {
              type: 'permissions',
              key: Entity.BLENDING,
            },
            showInSidebar: false,
          },
          {
            path: 'saved/:blendId',
            element: <SavedBlendDetails />,
            guard: {
              type: 'permissions',
              key: Entity.BLENDING,
            },
            showInSidebar: false,
          },
        ],
      },
      {
        path: 'products',
        label: 'Products',
        element: <Analytics mode="products" />,
        key: 'products',
        icon: BlendsIcon,
        showInSidebar: true,
        module: SidebarModuleFilter.Bakers,
        guard: {
          type: 'permissions',
          key: Entity.BAKER_ANALYTICS,
        },
      },
      {
        path: '/products/:productId',
        element: <AnalyticsDetails mode="products" />,
        guard: {
          type: 'permissions',
          key: Entity.BAKER_ANALYTICS,
        },
        showInSidebar: false,
      },
      {
        path: 'flours',
        guard: {
          type: 'settings',
          key: 'SHOW_FLOURS',
        },
        showInSidebar: true,
        module: SidebarModuleFilter.Wheat,
        permissions: {
          entity: Entity.FLOURS,
          action: CrudAction.GET,
        },
        children: [
          {
            path: '',
            redirect: 'production-line',
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Flours',
            key: RouteKeys.Flours,
            icon: FlourIcon,
            settings: ['SHOW_FLOURS'],
            permissions: {
              entity: Entity.FLOURS,
              action: CrudAction.GET,
            },
          },
          {
            path: 'production-line',
            element: <Analytics mode="production-line" />,
            guard: {
              type: 'permissions',
              key: Entity.FLOURS,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Production Line',
            key: RouteKeys.ProductionLine,
            icon: TruckIcon,
            settings: ['SHOW_FLOURS'],
            permissions: {
              entity: Entity.FLOURS,
              action: CrudAction.GET,
            },
          },
          {
            path: 'post-aging',
            element: <Analytics mode="post-aging" />,
            guard: {
              type: 'permissions',
              key: Entity.FLOURS,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Post Aging',
            key: RouteKeys.PostAging,
            icon: TruckIcon,
            notAvailable: isProd(),
            settings: ['SHOW_FLOURS'],
            permissions: {
              entity: Entity.FLOURS,
              action: CrudAction.GET,
            },
          },
          {
            path: 'production-line/:productId',
            element: <AnalyticsDetails mode="production-line" />,
            guard: {
              type: 'permissions',
              key: Entity.FLOURS,
            },
            showInSidebar: false,
          },
          {
            path: 'post-aging/:productId',
            element: <AnalyticsDetails mode="post-aging" />,
            guard: {
              type: 'permissions',
              key: Entity.FLOURS,
            },
            showInSidebar: false,
          },
        ],
      },
      {
        path: 'data',
        element: <Data />,
        guard: {
          type: 'settings',
          key: 'SHOW_WHEAT_DATA_ANALYSIS',
        },
        showInSidebar: true,
        module: SidebarModuleFilter.Wheat,
        label: 'Data',
        key: RouteKeys.Data,
        icon: DataIcon,
        settings: ['SHOW_WHEAT_DATA_ANALYSIS'],
        children: [
          {
            path: '',
            element: <Navigate to="shipments" replace />,
            showInSidebar: false,
          },
          {
            path: 'shipments',
            element: <ShipmentsData />,
            showInSidebar: false,
            module: SidebarModuleFilter.Wheat,
            key: RouteKeys.ShipmentsData,
            settings: ['SHOW_WHEAT_DATA_ANALYSIS'],
          },
          {
            path: 'storage',
            element: <StorageData />,
            showInSidebar: false,
            module: SidebarModuleFilter.Wheat,
            key: RouteKeys.StorageData,
            settings: ['SHOW_WHEAT_DATA_ANALYSIS'],
          },
          {
            path: 'blends',
            element: <BlendsData />,
            showInSidebar: false,
            module: SidebarModuleFilter.Wheat,
            key: RouteKeys.BlendsData,
            settings: ['SHOW_WHEAT_DATA_ANALYSIS'],
          },
          {
            path: 'flours',
            element: <FloursData />,
            showInSidebar: false,
            module: SidebarModuleFilter.Wheat,
            key: RouteKeys.FlourData,
            settings: ['SHOW_WHEAT_DATA_ANALYSIS'],
          },
        ],
      },
      {
        path: 'bakers/data',
        element: <UnavailablePage />,
        guard: {
          type: 'settings',
          key: 'SHOW_BAKER_DATA_ANALYSIS',
        },
        showInSidebar: true,
        module: SidebarModuleFilter.Bakers,
        label: 'Data',
        key: RouteKeys.BakersData,
        icon: DataIcon,
        notAvailable: true,
        settings: ['SHOW_BAKER_DATA_ANALYSIS'],
      },
      {
        path: 'settings',
        showInSidebar: true,
        module: SidebarModuleFilter.All,
        label: 'Settings',
        key: RouteKeys.SiteDetails,
        icon: SettingsIcon,
        children: [
          {
            path: 'site-details',
            element: <SiteDetails />,
            guard: {
              type: 'permissions',
              key: Entity.COMPANIES,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.All,
            label: 'Sites',
            key: RouteKeys.SiteDetails,
            icon: TruckIcon,
            permissions: {
              entity: Entity.COMPANIES,
              action: CrudAction.GET,
            },
          },
          {
            path: 'equinom-bins',
            element: <EquinomBins />,
            guard: {
              type: 'permissions',
              key: Entity.BINS,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Equinom Bins',
            key: RouteKeys.EquinomBins,
            icon: TruckIcon,
            settings: ['SHOW_WHEAT'],
            permissions: {
              entity: Entity.BINS,
              action: CrudAction.GET,
            },
          },
          {
            path: 'wheat-codes',
            element: (
              <WithWheatPolling>
                <WheatCodes />
              </WithWheatPolling>
            ),
            guard: {
              type: 'permissions',
              key: Entity.WHEAT_CODE,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Wheat Codes',
            key: RouteKeys.WheatCodes,
            icon: TruckIcon,
            settings: ['SHOW_WHEAT'],
            permissions: {
              entity: Entity.WHEAT_CODE,
              action: CrudAction.GET,
            },
          },
          {
            path: 'wheat/products',
            element: <WheatProductsPage />,
            guard: {
              type: 'permissions',
              key: Entity.PRODUCT,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Wheat,
            label: 'Blending Spec',
            key: RouteKeys.WheatProducts,
            settings: ['SHOW_WHEAT'],
            permissions: {
              entity: Entity.PRODUCT,
              action: CrudAction.GET,
            },
          },
          {
            path: 'bakers/products',
            element: <BakersProductsPage />,
            guard: {
              type: 'permissions',
              key: Entity.PRODUCT_BAKERS,
            },
            showInSidebar: true,
            module: SidebarModuleFilter.Bakers,
            label: 'Products',
            key: RouteKeys.BakersProducts,
            settings: ['SHOW_BAKER_MODULE'],
            permissions: {
              entity: Entity.PRODUCT_BAKERS,
              action: CrudAction.GET,
            },
          },
          {
            path: 'flours',
            element: <FlourProductsPage />,
            guard: {
              type: 'settings',
              key: 'SHOW_FLOURS',
            },
            module: SidebarModuleFilter.Wheat,
            label: 'Flours',
            key: RouteKeys.FlourProducts,
            settings: ['SHOW_FLOURS'],
            showInSidebar: true,
            permissions: {
              entity: Entity.PRODUCT_FLOUR,
              action: CrudAction.GET,
            },
          },
          {
            path: 'device-types',
            element: <DeviceTypes />,
            showInSidebar: true,
            module: SidebarModuleFilter.All,
            label: 'Device Types',
            key: RouteKeys.DeviceTypes,
            //TODO: handle permissions
          },
          {
            path: 'device-sn',
            element: <DeviceSN />,
            showInSidebar: true,
            module: SidebarModuleFilter.All,
            label: 'Device SN',
            key: RouteKeys.DeviceSN,
            //TODO: handle permissions
          },
        ],
      },
      {
        path: 'users',
        element: <Users />,
        guard: {
          type: 'permissions',
          key: Entity.USERS,
        },
        showInSidebar: true,
        module: SidebarModuleFilter.All,
        label: 'Users',
        key: RouteKeys.Users,
        icon: UserIcon,
        permissions: {
          entity: Entity.USERS,
          action: CrudAction.GET,
        },
      },
      {
        path: 'admin',
        element: <Admin />,
        guard: {
          type: 'permissions',
          key: Entity.ADMIN,
        },
        showInSidebar: true,
        module: SidebarModuleFilter.Wheat,
        label: 'Admin',
        key: 'admin',
        icon: AdminIcon,
        notAvailable: isProd(),
        permissions: {
          entity: Entity.ADMIN,
          action: CrudAction.GET,
        },
      },
      {
        path: 'system_settings',
        element: <SystemSettings />,
        guard: {
          type: 'permissions',
          key: Entity.ADMIN,
        },
        showInSidebar: true,
        module: SidebarModuleFilter.All,
        label: 'System Settings',
        key: 'system_settings',
        icon: ParametersIcon,
        permissions: {
          entity: Entity.ADMIN,
          action: CrudAction.GET,
        },
      },
      {
        path: 'no-permission',
        element: <NoPermission />,
        showInSidebar: false,
      },
      {
        path: '*',
        element: <PageNotFound />,
        showInSidebar: false,
      },
    ],
  },
]

export const generateRouter = (showBakerModule: boolean) => {
  const renderRouteElement = (route: RouteConfig) => {
    let element = route.element
    if (route.redirect) element = <Navigate to={route.redirect} />
    if (route.wrapper && element) {
      const WrapperComponent = route.wrapper
      element = <WrapperComponent>{element}</WrapperComponent>
    }
    if (route.guard) {
      const guardContent = element || <Outlet />
      if (route.guard.type === 'settings') {
        element = (
          <SettingsRouteGuard settingsKey={route.guard.key as any}>
            {guardContent}
          </SettingsRouteGuard>
        )
      } else if (route.guard.type === 'permissions') {
        element = (
          <PermissionsRouteGuard
            permissionKey={route.guard.key as Entity}
            requiredAction={
              Array.isArray(route.guard.action)
                ? route.guard.action[0]
                : (route.guard.action as any)
            }
          >
            {guardContent}
          </PermissionsRouteGuard>
        )
      }
    }
    return element
  }

  const renderRoute = (route: RouteConfig): ReactNode => {
    if (isModuleHidden(showBakerModule, route.module)) {
      return null
    }

    const filteredChildren = (route.children ?? [])
      .map((child) => renderRoute(child))
      .filter((child): child is ReactNode => child !== null)

    return (
      <ReactRouterRoute
        key={route.path}
        path={route.path}
        element={renderRouteElement(route)}
      >
        {filteredChildren.length > 0 && filteredChildren}
      </ReactRouterRoute>
    )
  }

  return createBrowserRouter(
    createRoutesFromElements(<>{routesConfig.map(renderRoute)}</>),
    {
      future: {
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
      },
    }
  )
}

export const getSidebarRoutes = (
  settings: Record<string, boolean | number | string> = {}
) => {
  const extractSidebarRoutes = (
    routes: RouteConfig[],
    parentPath: string = '',
    settings: Record<string, boolean | number | string> = {}
  ): RouteConfig[] => {
    if (!routes || !Array.isArray(routes)) return []

    const result: RouteConfig[] = []
    const showBakerModule = settings['SHOW_BAKER_MODULE'] === true

    routes.forEach((route) => {
      const fullPath = parentPath ? `${parentPath}/${route.path}` : route.path

      if (isModuleHidden(showBakerModule, route.module)) {
        return
      }

      // Special handling for the "flours" parent: collapse its visible children under a single parent entry
      if (route.path === 'flours') {
        const floursParent = route.children?.find(
          (child) =>
            child.path === '' && child.showInSidebar && child.label === 'Flours'
        )

        if (floursParent && route.children) {
          const sidebarChildren = route.children
            .filter((child) => {
              if (!child.showInSidebar || child.path === '') return false
              return !isModuleHidden(showBakerModule, child.module)
            })
            .map((child) => ({ ...child }))

          if (sidebarChildren.length > 0) {
            result.push({
              ...floursParent,
              path: route.path,
              children: sidebarChildren,
            })
          }
          return
        }
      }

      if (route.showInSidebar) {
        const children = route.children
          ? extractSidebarRoutes(route.children, fullPath, settings)
          : undefined

        result.push({
          ...route,
          children: children && children.length > 0 ? children : undefined,
        })
      } else if (route.children) {
        const childRoutes = extractSidebarRoutes(
          route.children,
          fullPath,
          settings
        )
        if (childRoutes.length > 0) {
          result.push(...childRoutes)
        }
      }
    })

    return result
  }

  return extractSidebarRoutes(routesConfig, '', settings)
}
